import { Node, findParentNode, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    expandable: {
      insertExpandable: (attrs?: {
        open?: boolean;
        id?: string | null;
      }) => ReturnType;
      toggleExpandableOpen: () => ReturnType;
    };
  }
}

export interface ExpandableOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

function genId(): string {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof (crypto as any).randomUUID === 'function'
    ) {
      return (crypto as any).randomUUID().replace(/-/g, '').slice(0, 8);
    }
  } catch {
    // noop
  }
  return Math.random().toString(36).slice(2, 10);
}

export const Expandable = Node.create<ExpandableOptions>({
  name: 'expandable',
  group: 'block',
  content: 'expandableSummary expandableContent',
  defining: true,
  isolating: true,
  // @ts-ignore
  allowGapCursor: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      open: {
        default: false,
        parseHTML: (el) => el.hasAttribute('open'),
        renderHTML: (attrs) => (attrs.open ? { open: '' } : {}),
      },
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('id'),
        renderHTML: (attrs) => (attrs.id ? { id: attrs.id } : {}),
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-type="expandable"]', priority: 60 },
      { tag: 'details[data-type="expandable"]', priority: 60 },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addCommands() {
    return {
      insertExpandable:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              open: attrs?.open ?? false,
              id: attrs?.id ?? genId(),
            },
            content: [
              { type: 'expandableSummary' },
              {
                type: 'expandableContent',
                content: [{ type: 'paragraph' }],
              },
            ],
          });
        },
      toggleExpandableOpen:
        () =>
        ({ state, chain }) => {
          const parent = findParentNode((n) => n.type === this.type)(
            state.selection,
          );
          if (!parent) return false;
          return chain()
            .command(({ tr }) => {
              tr.setNodeMarkup(parent.pos, undefined, {
                ...parent.node.attrs,
                open: !parent.node.attrs.open,
              });
              return true;
            })
            .run();
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => this.editor.commands.insertExpandable(),
      'Mod-.': () => this.editor.commands.toggleExpandableOpen(),
      Tab: () => {
        const { state } = this.editor;
        const { $from } = state.selection;
        // Only hijack Tab when cursor is inside an expandable subtree.
        let inside = false;
        for (let d = $from.depth; d > 0; d--) {
          const n = $from.node(d).type.name;
          if (n === 'expandableSummary' || n === 'expandableContent') {
            inside = true;
            break;
          }
        }
        if (!inside) return false;
        return (this.editor.commands as any).setHardBreak?.() ?? false;
      },
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    // @ts-ignore
    this.editor.isInitialized = true;
    return ReactNodeViewRenderer(this.options.view);
  },
});

export interface ExpandableSummaryOptions {
  HTMLAttributes: Record<string, any>;
}

export const ExpandableSummary = Node.create<ExpandableSummaryOptions>({
  name: 'expandableSummary',
  content: 'inline*',
  defining: true,
  selectable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-type="expandableSummary"]' },
      { tag: 'summary[data-type="expandableSummary"]' },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { state, view } = this.editor;
        const { $from } = state.selection;
        if ($from.parent.type.name !== this.name) return false;
        // Jump into the sibling expandableContent's first child.
        let expandableDepth = -1;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'expandable') {
            expandableDepth = d;
            break;
          }
        }
        if (expandableDepth < 0) return false;
        const expandablePos = $from.before(expandableDepth);
        const expandable = state.doc.nodeAt(expandablePos);
        if (!expandable) return false;
        // Open the parent if closed.
        const tr = state.tr;
        if (!expandable.attrs.open) {
          tr.setNodeMarkup(expandablePos, undefined, {
            ...expandable.attrs,
            open: true,
          });
        }
        let contentPos: number | null = null;
        expandable.forEach((child, offset) => {
          if (contentPos == null && child.type.name === 'expandableContent') {
            // +1 for expandable opening, +1 for expandableContent opening, +1 for first child opening
            contentPos = expandablePos + 1 + offset + 2;
          }
        });
        if (contentPos == null) return false;
        tr.setSelection(TextSelection.near(tr.doc.resolve(contentPos)));
        view.dispatch(tr);
        return true;
      },
    };
  },
});

export interface ExpandableContentOptions {
  HTMLAttributes: Record<string, any>;
}

export const ExpandableContent = Node.create<ExpandableContentOptions>({
  name: 'expandableContent',
  content: 'block+',
  defining: true,
  selectable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="expandableContent"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },
});
